import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import { fadeUp, stagger } from "@/lib/animations";

const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="text-xl font-bold text-neutral-900">{title}</h2>
    <div className="mt-3 space-y-3 text-neutral-600">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  usePageSeo(pageSeo.privacy);
  return (
    <div className="min-h-screen">
      {/* ── Hero ── */}
      <section className="w-full border-b border-neutral-100 bg-gradient-to-b from-white to-stone-50 py-14">
        <motion.div
          className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 variants={fadeUp} className="text-3xl font-bold text-neutral-900">Privacy Policy</motion.h1>
          <motion.p variants={fadeUp} className="mt-3 text-sm text-neutral-500">
            Last updated: 20 May 2026
          </motion.p>
          <motion.p variants={fadeUp} className="mt-4 leading-relaxed text-neutral-600">
            This Privacy Policy explains how Arsoc collects, uses, and protects your
            personal data when you interact with our website and products. We are committed
            to handling your information responsibly and transparently.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Body ── */}
      <section className="w-full py-14">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">

            <Section id="who-we-are" title="1. Who we are">
              <p>
                Arsoc is a software development company registered and operating in
                Zimbabwe. We build and operate business management platforms, including
                Alpha and Estman.
              </p>
              <p>
                <strong>Data controller:</strong> Arsoc<br />
                <strong>Contact:</strong>{" "}
                <a
                  href="mailto:admin@arsoc.co.zw"
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  admin@arsoc.co.zw
                </a>
              </p>
              <p>
                If you have any questions about this policy or about how your data is
                handled, please contact us at the email address above.
              </p>
            </Section>

            <Section id="data-we-collect" title="2. Data we collect">
              <p>We collect personal data in the following categories, depending on how you interact with us:</p>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-neutral-800">a) Account and registration data</p>
                  <p>
                    When an organisation registers to use one of our products (Alpha or
                    Estman), we collect information about the organisation and the individual
                    creating the account: name, business name, email address, and contact
                    details.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">b) Contact enquiry data</p>
                  <p>
                    When you contact us via the form on our website or by email, we collect
                    the information you provide: your name, email address, company name (if
                    given), and the content of your message.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">c) End-user data within Alpha</p>
                  <p>
                    Alpha enables businesses to manage customer interactions via WhatsApp.
                    When end-users communicate with an Alpha-enabled business over WhatsApp,
                    the content of those conversations — including messages, customer names,
                    and transaction information — is processed through the Alpha platform.
                    This data belongs to and is controlled by the business operating the
                    Alpha account. Arsoc processes this data on their behalf as a data
                    processor.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">d) Usage and technical data</p>
                  <p>
                    We may collect technical information when you use our products or visit
                    our website, including IP addresses, browser type, pages visited, and
                    timestamps. This data is used for security, debugging, and improving our
                    services.
                  </p>
                </div>
              </div>
            </Section>

            <Section id="how-we-use-data" title="3. How we use your data">
              <p>We use the data we collect for the following purposes:</p>
              <ul className="ml-4 list-disc space-y-1.5">
                <li>To provide and operate our products and services</li>
                <li>To respond to enquiries and provide customer support</li>
                <li>To manage accounts and verify identity</li>
                <li>To improve and develop our products based on usage patterns</li>
                <li>To meet legal and regulatory obligations</li>
                <li>To protect the security and integrity of our systems</li>
              </ul>
              <p>
                We do not sell your personal data to third parties. We do not use personal
                data for advertising purposes unrelated to the services you have engaged
                with.
              </p>
            </Section>

            <Section id="lawful-basis" title="4. Lawful basis for processing">
              <p>
                Where data protection laws require a lawful basis for processing personal
                data, we rely on the following:
              </p>
              <ul className="ml-4 list-disc space-y-1.5">
                <li>
                  <strong>Contract:</strong> Processing necessary to provide the services
                  you have signed up for
                </li>
                <li>
                  <strong>Legitimate interests:</strong> For security, fraud prevention,
                  and improving our products — where those interests are not overridden by
                  your privacy rights
                </li>
                <li>
                  <strong>Consent:</strong> Where we explicitly ask for your consent, such
                  as for optional communications
                </li>
                <li>
                  <strong>Legal obligation:</strong> Where we are required by law to retain
                  or disclose data
                </li>
              </ul>
            </Section>

            <Section id="third-parties" title="5. Data sharing and third parties">
              <p>
                We share personal data with third parties only where necessary to deliver
                our services or as required by law. The third parties we work with include:
              </p>
              <div className="space-y-3">
                <div>
                  <p className="font-semibold text-neutral-800">Meta (WhatsApp Business Platform)</p>
                  <p>
                    Alpha uses the WhatsApp Business API, operated by Meta Platforms, Inc.,
                    to enable customer communication. When end-users interact with an
                    Alpha-enabled business via WhatsApp, those messages are transmitted
                    through Meta's infrastructure. Meta's data handling is governed by their
                    own{" "}
                    <a
                      href="https://www.whatsapp.com/legal/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2 hover:opacity-80"
                    >
                      WhatsApp Privacy Policy
                    </a>
                    .
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">Hosting and infrastructure providers</p>
                  <p>
                    Our products and website are hosted on cloud infrastructure providers
                    including Neon (database hosting) and Vercel (application hosting).
                    These providers store and process data on our behalf under data
                    processing agreements.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">Professional and legal advisors</p>
                  <p>
                    In limited circumstances, we may share data with legal, financial, or
                    regulatory advisors where required.
                  </p>
                </div>
              </div>
            </Section>

            <Section id="data-retention" title="6. Data retention">
              <p>
                We retain personal data for as long as is necessary for the purposes
                described in this policy, or as required by applicable law.
              </p>
              <ul className="ml-4 list-disc space-y-1.5">
                <li>
                  Account data is retained for the duration of the customer relationship
                  and for a reasonable period after account closure to meet legal and
                  financial record-keeping obligations.
                </li>
                <li>
                  Contact enquiry data is retained for up to 24 months, after which it
                  is deleted or anonymised.
                </li>
                <li>
                  End-user data processed within Alpha is retained according to the data
                  retention policies of the business operating the account. Arsoc will
                  delete this data upon termination of the business's contract.
                </li>
              </ul>
            </Section>

            <Section id="your-rights" title="7. Your rights">
              <p>
                Depending on your location and applicable law, you may have the following
                rights regarding your personal data:
              </p>
              <ul className="ml-4 list-disc space-y-1.5">
                <li>
                  <strong>Access:</strong> The right to request a copy of the personal data
                  we hold about you
                </li>
                <li>
                  <strong>Correction:</strong> The right to request correction of inaccurate
                  or incomplete data
                </li>
                <li>
                  <strong>Deletion:</strong> The right to request deletion of your personal
                  data where we no longer have a lawful basis to retain it
                </li>
                <li>
                  <strong>Objection:</strong> The right to object to processing based on
                  legitimate interests
                </li>
                <li>
                  <strong>Portability:</strong> The right to receive your data in a
                  structured, machine-readable format
                </li>
              </ul>
              <p>
                To exercise any of these rights, or to request deletion of your data,
                please visit our{" "}
                <Link
                  to="/data-deletion"
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  Data Deletion page
                </Link>{" "}
                or contact us at{" "}
                <a
                  href="mailto:admin@arsoc.co.zw"
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  admin@arsoc.co.zw
                </a>
                . We will respond within 30 days.
              </p>
            </Section>

            <Section id="cookies" title="8. Cookies and tracking">
              <p>
                Our website may use cookies and similar technologies for basic functionality
                and to understand how visitors use the site. We do not use advertising
                cookies or cross-site tracking.
              </p>
              <p>
                You can control cookie settings through your browser. Disabling cookies
                may affect the functionality of some parts of the site.
              </p>
            </Section>

            <Section id="security" title="9. Security">
              <p>
                We implement appropriate technical and organisational measures to protect
                personal data against unauthorised access, alteration, disclosure, or
                destruction. These include encrypted data storage and transmission, access
                controls, and regular security reviews.
              </p>
              <p>
                No system is entirely secure. If you believe your data has been
                compromised, please contact us immediately at{" "}
                <a
                  href="mailto:admin@arsoc.co.zw"
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  admin@arsoc.co.zw
                </a>
                .
              </p>
            </Section>

            <Section id="changes" title="10. Changes to this policy">
              <p>
                We may update this Privacy Policy from time to time. When we make material
                changes, we will update the "Last updated" date at the top of this page.
                We encourage you to review this policy periodically.
              </p>
              <p>
                Continued use of our services after any changes constitutes your acceptance
                of the updated policy.
              </p>
            </Section>

            <Section id="contact" title="11. Contact us">
              <p>
                For any questions, concerns, or requests relating to this Privacy Policy
                or the handling of your personal data, please contact us at:
              </p>
              <address className="not-italic rounded-lg border border-neutral-100 bg-stone-50 p-4 text-sm">
                <strong>Arsoc</strong><br />
                150 Nelson Mandela Avenue, Harare, Zimbabwe<br />
                <a
                  href="mailto:admin@arsoc.co.zw"
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  admin@arsoc.co.zw
                </a>
              </address>
            </Section>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
